/**
 * Columns of the AI Analytics report — events no built-in report covers (panel OCR, belt
 * movement, worker restarts, and any model added later). One definition for the local and the
 * cloud report page.
 *
 * Nothing here knows a model. Every model reports different values, so the "Reported values"
 * column lists whatever the event's metadata holds ("value 160 · threshold 180 · low_for_s
 * 302"), and the type columns show the exact event_type / event_subtype strings the AI module
 * sent next to their readable names — the same strings an alert rule matches on.
 */

/** Metadata keys that are bookkeeping, not findings. `t` is the model's own clock. */
const HIDDEN_KEYS = new Set(['t', 'camera_id', 'event_type', 'timestamp']);

const titleCase = (s: string) =>
  s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (m) => m.toUpperCase());

function asObject(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  if (typeof v === 'string') {
    try {
      const o = JSON.parse(v);
      return o && typeof o === 'object' && !Array.isArray(o) ? o : null;
    } catch { return null; }
  }
  return null;
}

function scalar(v: unknown): string {
  if (typeof v === 'number') return String(Math.round(v * 1000) / 1000);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** "Panel Reading  ·  panel_reading" — readable name and the exact string. */
export function aiEventTypeText(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '—';
  return `${titleCase(raw)}  ·  ${raw}`;
}

/** The event's reported values, one "key value" pair each. */
export function reportedValues(metadata: unknown): [string, string][] {
  const m = asObject(metadata);
  if (!m) return [];
  return Object.entries(m)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && (typeof v !== 'object' || v === null))
    .map(([k, v]) => [k, scalar(v)]);
}

function formatTime(epoch: unknown): string {
  const n = Number(epoch);
  if (!n) return '—';
  const d = new Date(n * 1000);
  return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

/** Confidence from the row, or from what the model reported (`conf`, `confidence`). */
function confidenceOf(row: any): number | null {
  const m = asObject(row?.metadata) || {};
  const c = row?.confidence ?? m.confidence ?? m.conf;
  const n = Number(c);
  return c === null || c === undefined || !Number.isFinite(n) ? null : n;
}

export const AI_ANALYTICS_COLUMNS = [
  { key: 'image_paths', label: 'Images', type: 'images' },
  { key: 'event_timestamp', label: 'Detected At', type: 'datetime', render: (v: unknown) => formatTime(v) },
  { key: 'camera_id', label: 'Camera', type: 'camera' },
  { key: 'event_type', label: 'Model (event_type)', render: (v: unknown) => aiEventTypeText(v) },
  { key: 'sub_type', label: 'Event (event_subtype)', render: (v: unknown) => aiEventTypeText(v) },
  {
    key: 'metadata',
    label: 'Reported values',
    wide: true,
    render: (v: unknown) => {
      const pairs = reportedValues(v);
      return pairs.length ? pairs.map(([k, val]) => `${k} ${val}`).join('  ·  ') : '—';
    },
  },
  {
    key: 'confidence',
    label: 'Confidence',
    render: (_v: unknown, row: any) => {
      const c = confidenceOf(row);
      return c === null ? '—' : `${(c <= 1 ? c * 100 : c).toFixed(0)}%`;
    },
  },
  { key: 'severity', label: 'Severity', render: (v: unknown) => (v ? titleCase(String(v)) : '—') },
  { key: 'event_id', label: 'Event ID', detailOnly: true },
];
